import { useState, useEffect, useRef, useCallback } from 'react';
import { getData, putDataWithBody, postDataWithBody } from '../../../Services/Services';
import { ackupdate, getalertsall, getgeofenceall, fetchVehicleTracking } from '../../../Services/EndPoints';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import './Geofence.css';

// ── Constants ──────────────────────────────────────────────────────────────────
const TEAL = '#0f4a45';
const GOOGLE_MAPS_API_KEY = 'AIzaSyDLzC-FhQmB8JS2H46OW46DNOEboBbrZVg';

const GEO_LAYER_OPTIONS = [
  { id: 'roadmap',   label: 'Roadmap',   icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18"/></svg> },
  { id: 'satellite', label: 'Satellite', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg> },
  { id: 'hybrid',    label: 'Hybrid',    icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5M2 12l10 5 10-5"/></svg> },
  { id: 'terrain',   label: 'Terrain',   icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 18l-7-7-5 5-5-5"/><path d="M2 18h20"/></svg> },
];

const GEOFENCE_COLS = [
  { key: 'name',      label: 'Geofence Name', sortable: true  },
  { key: 'type',      label: 'Boundary Type', sortable: false },
  { key: 'vehicles',  label: 'Vehicles',      sortable: false },
  { key: 'alertType', label: 'Alert Type',    sortable: false },
  { key: 'status',    label: 'Status',        sortable: true  },
  { key: 'actions',   label: 'Actions',       sortable: false },
];

const geocache = new Map();

const reverseGeocode = async (lat, lng) => {
  const cacheKey = `${lat},${lng}`;
  if (geocache.has(cacheKey)) return geocache.get(cacheKey);
  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
    );
    if (!response.ok) throw new Error('Geocoding failed');
    const data = await response.json();
    if (data.status === 'OK' && data.results.length > 0) {
      const addressComponents = data.results[0].address_components;
      const getComponent = (types) => {
        const c = addressComponents.find(c => types.some(t => c.types.includes(t)));
        return c ? c.long_name : '';
      };
      const parts = [];
      const s2 = getComponent(['sublocality_level_2']);
      const s1 = getComponent(['sublocality_level_1']);
      const loc = getComponent(['locality']);
      const state = getComponent(['administrative_area_level_1']);
      const pin = getComponent(['postal_code']);
      if (s2) parts.push(s2);
      if (s1) parts.push(s1);
      if (loc) parts.push(loc);
      const sp = [state, pin].filter(Boolean).join(' ');
      if (sp) parts.push(sp);
      const shortAddress = parts.join(', ');
      geocache.set(cacheKey, shortAddress);
      return shortAddress;
    }
    return `${parseFloat(lat).toFixed(5)}, ${parseFloat(lng).toFixed(5)}`;
  } catch {
    return `${parseFloat(lat).toFixed(5)}, ${parseFloat(lng).toFixed(5)}`;
  }
};

const ALERT_COLS = [
  { key: 'alertRefId', label: 'Alert ID',           sortable: true  },
  { key: 'vehicle',    label: 'Vehicle Number',     sortable: true  },
  { key: 'geofence',   label: 'Geofence Name',            sortable: true  },
  { key: 'event',      label: 'Event',               sortable: false },
  { key: 'address',    label: 'Address',             sortable: false },
  { key: 'time',       label: 'Alert Date and Time', sortable: true  },
  { key: 'acked',      label: 'Status',              sortable: false },
  { key: 'action',     label: 'Action',              sortable: false },
];

const getStatusFromActive = (active) => (active ? 'active' : 'inactive');

const normaliseAlert = (raw, idx) => {
  const coordinates = (raw.latitude && raw.longitude)
    ? { lat: raw.latitude, lng: raw.longitude }
    : null;
  return {
    id: raw.id ?? `alert-${idx}`,
    alertRefId: raw.alertRefId || '—',
    vehicle: raw.vehicleNumber || '—',
    geofence: raw.geolocationName || '—',
    event: raw.alertType === 'IN' ? 'ENTERED' : raw.alertType === 'OUT' ? 'EXITED' : (raw.alertType || 'EXITED').toUpperCase(),
    coordinates,
    address: coordinates
      ? `${parseFloat(coordinates.lat).toFixed(5)}, ${parseFloat(coordinates.lng).toFixed(5)}`
      : '—',
    rawTime: raw.createdOn || 0,
    time: raw.createdOn
      ? new Date(raw.createdOn).toLocaleString('en-IN', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit', hour12: true
        }).replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$1-$2-$3')
          .replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase())
      : '—',
    acked: raw.isAcknowledged ?? false,
    rawData: raw,
  };
};

// Alerts whose alertType is one of these live in the "Deviation Alerts" tab instead
// of the regular "Alerts" tab — normaliseAlert already uppercases alertType into `event`.
const DEVIATION_ALERT_TYPES = ['ROUTE_DEVIATION', 'HALT_EXCEEDED', 'HALT_MISSED', 'EXTRA_HALT'];
const isDeviationAlert = (alert) => DEVIATION_ALERT_TYPES.includes(alert.event);

const getGeofenceStats = (geofences) => [
  { icon: '📍', value: geofences.length, label: 'Total Geofences', color: '#0f4a45' },
  { icon: '🛡️', value: geofences.filter(g => g.status === 'active').length, label: 'Active Fences', color: '#16a34a' },
  { icon: '⭕', value: geofences.filter(g => g.status !== 'active').length, label: 'Inactive Fences', color: '#6b7280' },
  { icon: '🚗', value: geofences.reduce((s, g) => s + (g.vehicleCount || 0), 0), label: 'Monitored Vehicles', color: '#1a7a73' },
];

const getAlertStats = (alerts) => [
  { icon: '🔔', value: alerts.length, label: 'Total Alerts', color: '#0f4a45' },
  { icon: '⚠️', value: alerts.filter(a => !a.acked).length, label: 'Pending Alerts', color: '#dc2626' },
  { icon: '✅', value: alerts.filter(a => a.acked).length, label: 'Acknowledged', color: '#16a34a' },
  { icon: '🚦', value: alerts.filter(a => a.event === 'ENTERED').length, label: 'Entry Events', color: '#1a7a73' },
];

// ── Small UI Components ────────────────────────────────────────────────────────
const StatCard = ({ icon, value, label, color }) => (
  <div style={styles.statCard} className="gf-stat-card">
    <div
      style={{ ...styles.statIcon, background: `${color}18`, color }}
      className="gf-stat-icon"
    >
      <span style={{ fontSize: 24 }}>{icon}</span>
    </div>
    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, flexWrap: 'nowrap', overflow: 'hidden' }}>
        <div style={styles.statValue}>{value}</div>
        <div style={styles.statLabel}>{label}</div>
      </div>
    </div>
  </div>
);

const SortIcon = ({ column, sortCol, sortDir }) => {
  const active = sortCol === column;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', marginLeft: 5, gap: 1, verticalAlign: 'middle' }}>
      <span style={{ fontSize: 7, lineHeight: 1, color: active && sortDir === 'asc'  ? '#fff' : 'rgba(255,255,255,0.4)' }}>▲</span>
      <span style={{ fontSize: 7, lineHeight: 1, color: active && sortDir === 'desc' ? '#fff' : 'rgba(255,255,255,0.4)' }}>▼</span>
    </span>
  );
};

const Pagination = ({ total, page, pageSize, onPageChange, onPageSizeChange }) => {
  const [goInput, setGoInput] = useState('');
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const handleGo = (e) => {
    if (e.key === 'Enter') {
      const n = parseInt(goInput, 10);
      if (!isNaN(n) && n >= 1 && n <= totalPages) onPageChange(n);
      setGoInput('');
    }
  };
  return (
    <div style={pg.bar}>
      <span style={pg.info}>
        Showing <strong style={{ color: '#1e293b' }}>{from}</strong> – <strong style={{ color: '#1e293b' }}>{to}</strong> of <strong style={{ color: '#1e293b' }}>{total}</strong> entries
      </span>
      <div style={pg.controls}>
        <span style={pg.label}>Page:</span>
        <input
          style={pg.input}
          value={goInput}
          onChange={e => setGoInput(e.target.value)}
          onKeyDown={handleGo}
          placeholder={String(page)}
        />
        <select style={pg.select} value={pageSize} onChange={e => { onPageSizeChange(Number(e.target.value)); onPageChange(1); }}>
          {[5, 10, 20, 50].map(n => <option key={n} value={n}>Show {n}</option>)}
        </select>
        <button
          style={{ ...pg.navBtn, opacity: page <= 1 ? 0.35 : 1 }}
          onClick={() => page > 1 && onPageChange(page - 1)}
          disabled={page <= 1}
        >‹</button>
        <button style={pg.activeBtn}>{page}</button>
        <button
          style={{ ...pg.navBtn, opacity: page >= totalPages ? 0.35 : 1 }}
          onClick={() => page < totalPages && onPageChange(page + 1)}
          disabled={page >= totalPages}
        >›</button>
        <span style={pg.totalPages}>{page} / {totalPages}</span>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const statusLower = status?.toLowerCase() || 'inactive';
  const cfg = { active: { bg: '#dcfce7', color: '#16a34a' }, inactive: { bg: '#f3f4f6', color: '#6b7280' } }[statusLower] || { bg: '#f3f4f6', color: '#6b7280' };
  const displayStatus = status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : 'Inactive';
  return <span style={{ ...styles.badge, background: cfg.bg, color: cfg.color }}>{displayStatus}</span>;
};

// ── NEW: AlertTypeBadge ────────────────────────────────────────────────────────
const AlertTypeBadge = ({ type }) => {
  if (!type) return <span style={{ color: '#94a3b8', fontSize: 13 }}>—</span>;
  const isIn = type === 'IN';
  return (
    <span style={{
      padding: '3px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700,
      background: isIn ? '#dcfce7' : '#fee2e2',
      color: isIn ? '#16a34a' : '#dc2626',
      display: 'inline-flex', alignItems: 'center', gap: 5,
    }}>
      {isIn ? '↓ IN' : '↑ OUT'}
    </span>
  );
};

const EventBadge = ({ event }) => {
  const isEntry = event === 'ENTERED';
  return (
    <span style={{ ...styles.eventBadge, background: isEntry ? '#1e3a5f' : '#fee2e2', color: isEntry ? '#fff' : '#dc2626' }}>
      {event}
    </span>
  );
};

const TableLoader = ({ cols }) => (
  <tr>
    <td colSpan={cols} style={{ ...styles.td, textAlign: 'center', padding: '40px 16px' }}>
      <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 32, height: 32, border: '3px solid #e2e8f0', borderTop: `3px solid ${TEAL}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <span style={{ color: '#94a3b8', fontSize: 13 }}>Loading…</span>
      </div>
    </td>
  </tr>
);

// ── Acknowledge Confirmation Modal ─────────────────────────────────────────────
const AckConfirmModal = ({ alert, onConfirm, onCancel }) => {
  if (!alert) return null;
  return (
    <>
      <style>{`
        @keyframes ack-modal-in {
          from { opacity: 0; transform: scale(0.92) translateY(12px); }
          to   { opacity: 1; transform: scale(1)   translateY(0); }
        }
      `}</style>
      <div
        onClick={e => e.target === e.currentTarget && onCancel()}
        style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: 'rgba(15,23,42,0.65)',
          backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }}
      >
        <div style={{
          background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440,
          boxShadow: '0 32px 80px rgba(0,0,0,0.35)',
          animation: 'ack-modal-in 0.2s ease',
          overflow: 'hidden',
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e6b75 0%, #1e6b75 100%)',
            padding: '18px 22px',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: 'rgba(42,140,153,0.25)',
              border: '2px solid rgba(42,140,153,0.6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, flexShrink: 0,
            }}>✓</div>
            <div>
              <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 16 }}>Acknowledge Alert</div>
              <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>Confirm acknowledgement for this record</div>
            </div>
          </div>
          <div style={{ padding: '20px 22px' }}>
            <p style={{ color: '#374151', fontSize: 14, margin: '0 0 16px', lineHeight: 1.6 }}>
              Are you sure you want to acknowledge this alert?
            </p>
            <div style={{
              background: '#f8fafc', border: '1px solid #e2e8f0',
              borderRadius: 10, padding: '14px 16px',
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px',
            }}>
              {[
                { label: 'Vehicle',      value: alert.vehicle  },
                { label: 'Geofence',     value: alert.geofence },
                { label: 'Event',        value: alert.event    },
                { label: 'Date and Time',value: alert.time     },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{value}</div>
                </div>
              ))}
              <div style={{ gridColumn: '1 / -1' }}>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Location</div>
                <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{alert.address}</div>
              </div>
            </div>
          </div>
          <div style={{ padding: '14px 22px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              onClick={onCancel}
              style={{
                padding: '9px 22px', borderRadius: 8,
                border: '1.5px solid #e2e8f0', background: '#fff',
                color: '#64748b', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
              onMouseEnter={e => { e.target.style.background = '#f8fafc'; e.target.style.borderColor = '#cbd5e1'; }}
              onMouseLeave={e => { e.target.style.background = '#fff'; e.target.style.borderColor = '#e2e8f0'; }}
            >Cancel</button>
            <button
              onClick={onConfirm}
              style={{
                padding: '9px 28px', borderRadius: 8,
                border: 'none', background: TEAL,
                color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
              onMouseEnter={e => e.target.style.background = '#1e6b75'}
              onMouseLeave={e => e.target.style.background = TEAL}
            >✓ Yes, Acknowledge</button>
          </div>
        </div>
      </div>
    </>
  );
};

// ── Vehicle Live-Tracking Popup Modal ──────────────────────────────────────────
const VehicleTrackingModal = ({ alert, onClose }) => {
  const mapRef             = useRef(null);
  const mapInstanceRef     = useRef(null);
  const markerRef          = useRef(null);
  const overlayRef         = useRef(null);
  const polylineRef        = useRef(null);
  const geofenceOverlayRef = useRef(null);   // ← NEW
  const trackingIntervalRef  = useRef(null);
  const animationIntervalRef = useRef(null);
  const lastEndTimeRef       = useRef(null);

  const [mapReady,         setMapReady]         = useState(false);
  const [vehiclePos,       setVehiclePos]       = useState(null);
  const [isTracking,       setIsTracking]       = useState(false);
  const [noGpsData,        setNoGpsData]        = useState(true);
  const [activeLayer,      setActiveLayer]      = useState('roadmap');
  const [showLayerPanel,   setShowLayerPanel]   = useState(false);
  const [resolvedAddress,  setResolvedAddress]  = useState(alert.address);

  const vehicleNumber = alert.vehicle;
console.log("isTracking",isTracking)
  const formatDateTime = (date) => {
    const pad = n => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
           `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R    = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a    = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const validateGPSPoints = (points) => {
    if (!points || points.length === 0) return [];
    if (points.length <= 2) return points;
    const valid = [points[0]];
    for (let i = 1; i < points.length - 1; i++) {
      const cur  = points[i], prev = valid[valid.length - 1];
      const curLat  = parseFloat(cur.latitude),  curLng  = parseFloat(cur.longitude);
      const prevLat = parseFloat(prev.latitude), prevLng = parseFloat(prev.longitude);
      if (isNaN(curLat) || isNaN(curLng) || isNaN(prevLat) || isNaN(prevLng)) continue;
      const dist     = calculateDistance(prevLat, prevLng, curLat, curLng);
      const timeDiff = Math.abs(new Date(cur.vehicletrackingtime) - new Date(prev.vehicletrackingtime)) / 3600000;
      if (timeDiff < 0.001) continue;
      if (dist <= 5 && dist / timeDiff <= 200) valid.push(cur);
    }
    if (points.length > 1) valid.push(points[points.length - 1]);
    return valid;
  };

  const createPulseOverlay = useCallback((position, speed) => {
    if (!window.google) return null;
    const sp = parseFloat(speed) || 0;
    let speedColor = sp === 0 ? '#6b7280' : sp < 30 ? '#16a34a' : sp < 60 ? '#f59e0b' : '#dc2626';
    const speedLabel = sp === 0 ? 'STOP' : `${Math.round(sp)} km/h`;

    class PulseOverlay extends window.google.maps.OverlayView {
      constructor(pos) { super(); this.pos = pos; this.div = null; }
      onAdd() {
        const div = document.createElement('div');
        div.style.cssText = 'position:absolute;pointer-events:none;transform:translate(-50%,-50%);z-index:99998;';
        div.innerHTML = `
          <div style="position:relative;display:flex;align-items:center;justify-content:center;">
            <div style="position:absolute;width:24px;height:24px;border-radius:50%;background:${TEAL};opacity:0.3;animation:vt-pulse-ring 1.8s ease-out infinite;"></div>
            <div style="position:absolute;width:24px;height:24px;border-radius:50%;background:${TEAL};opacity:0.15;animation:vt-pulse-ring 1.8s ease-out 0.4s infinite;"></div>
            <div style="width:20px;height:20px;border-radius:50%;background:${speedColor};border:3px solid ${TEAL};position:relative;z-index:2;box-shadow:0 2px 8px rgba(0,0,0,0.3);"></div>
            <div style="position:absolute;top:-26px;left:50%;transform:translateX(-50%);white-space:nowrap;font-size:10px;font-weight:700;padding:2px 7px;border-radius:99px;color:#fff;background:${speedColor};box-shadow:0 2px 6px rgba(0,0,0,.25);">${speedLabel}</div>
          </div>`;
        this.div = div;
        this.getPanes().overlayMouseTarget.appendChild(div);
      }
      draw() {
        if (!this.div) return;
        const pt = this.getProjection().fromLatLngToDivPixel(this.pos);
        if (pt) { this.div.style.left = `${pt.x}px`; this.div.style.top = `${pt.y}px`; }
      }
      updatePosition(newPos) { this.pos = newPos; this.draw(); }
      onRemove() { if (this.div?.parentNode) { this.div.parentNode.removeChild(this.div); this.div = null; } }
    }
    return new PulseOverlay(new window.google.maps.LatLng(position[0], position[1]));
  }, []);

  // ── NEW: draw geofence boundary from alert.rawData.geofence ──────────────────
  const drawGeofenceBoundary = useCallback((map) => {
    if (!window.google || !map) return;
    const geo = alert.rawData?.geofence;
    if (!geo?.geometry) return;

    if (geofenceOverlayRef.current) {
      geofenceOverlayRef.current.setMap(null);
      geofenceOverlayRef.current = null;
    }

    const color    = geo.color || '#1a7a73';
    const geomType = geo.geometry.type;
    const bounds   = new window.google.maps.LatLngBounds();

    if (geomType === 'Circle') {
      const { lat, lng } = geo.geometry.center;
      const circle = new window.google.maps.Circle({
        strokeColor: color, strokeOpacity: 0.9, strokeWeight: 2,
        fillColor: color, fillOpacity: 0.15,
        map, center: { lat, lng }, radius: geo.geometry.radius,
      });
      geofenceOverlayRef.current = circle;

      // Name label marker
      new window.google.maps.Marker({
        position: { lat, lng }, map,
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 0 },
        label: { text: geo.name || 'Geofence', color: '#1e293b', fontSize: '12px', fontWeight: 'bold' },
        zIndex: 50,
      });

      const cb = circle.getBounds();
      if (cb) bounds.union(cb);

    } else if (geomType === 'Polygon' || geomType === 'polygon') {
      const coords = geo.geometry.coordinates;
      const path   = (Array.isArray(coords[0]) && Array.isArray(coords[0][0]) ? coords[0] : coords)
        .map(([lng, lat]) => ({ lat, lng }));
      if (path.length >= 3) {
        const polygon = new window.google.maps.Polygon({
          paths: path, strokeColor: color, strokeOpacity: 0.9, strokeWeight: 2,
          fillColor: color, fillOpacity: 0.15, map,
        });
        geofenceOverlayRef.current = polygon;
        path.forEach(p => bounds.extend(p));
      }

    } else if (['LineString', 'linestring', 'Polyline', 'polyline'].includes(geomType)) {
      const coords = geo.geometry.coordinates;
      const path   = coords.map(([lng, lat]) => ({ lat, lng }));
      if (path.length >= 2) {
        const polyline = new window.google.maps.Polyline({
          path, geodesic: true, strokeColor: color,
          strokeOpacity: 0.9, strokeWeight: 3, map,
        });
        geofenceOverlayRef.current = polyline;
        path.forEach(p => bounds.extend(p));
      }
    }

    // Extend bounds to include alert pin, then fit
    if (alert.coordinates) {
      bounds.extend({ lat: Number(alert.coordinates.lat), lng: Number(alert.coordinates.lng) });
    }
    if (!bounds.isEmpty()) map.fitBounds(bounds);
  }, [alert]);

  const fetchVehicleData = useCallback(async (startTime, endTime, map) => {
    try {
      const newData = await postDataWithBody(fetchVehicleTracking, { vehicleNumber, startTime, endTime }, {});
      lastEndTimeRef.current = endTime;
      if (!newData || newData.length === 0) return;

      const validated = validateGPSPoints(newData);
      if (validated.length === 0) return;

      const latest = validated[validated.length - 1];
      const latN   = parseFloat(latest.latitude);
      const lngN   = parseFloat(latest.longitude);

      setNoGpsData(false);
      setVehiclePos({
        latitude:  latN, longitude: lngN,
        speed:     latest.speed ?? '--',
        timestamp: latest.vehicletrackingtime,
      });

      reverseGeocode(latN, lngN).then(addr => { if (addr) setResolvedAddress(addr); });

      const coords = validated.map(p => [parseFloat(p.latitude), parseFloat(p.longitude)]);

      if (map && coords.length > 1 && window.google) {
        if (polylineRef.current) polylineRef.current.setMap(null);
        polylineRef.current = new window.google.maps.Polyline({
          path: coords.map(([lat, lng]) => ({ lat, lng })),
          geodesic: false, strokeColor: TEAL,
          strokeOpacity: 0.9, strokeWeight: 3, map, zIndex: 500,
        });
      }

      if (animationIntervalRef.current) { clearInterval(animationIntervalRef.current); animationIntervalRef.current = null; }
      let idx = 0;
      const moveMarker = () => {
        if (idx >= coords.length) { clearInterval(animationIntervalRef.current); return; }
        const pos = { lat: coords[idx][0], lng: coords[idx][1] };
        if (!markerRef.current && map && window.google) {
          const sp = parseFloat(latest.speed) || 0;
          const mc = sp === 0 ? '#6b7280' : sp < 30 ? '#16a34a' : sp < 60 ? '#f59e0b' : '#dc2626';
          markerRef.current = new window.google.maps.Marker({
            position: pos, map,
            icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: mc, fillOpacity: 1, strokeWeight: 2, strokeColor: TEAL, anchor: new window.google.maps.Point(0, 0) },
            title: vehicleNumber, zIndex: 99999,
          });
          if (!overlayRef.current) {
            overlayRef.current = createPulseOverlay(coords[0], latest.speed);
            if (overlayRef.current) overlayRef.current.setMap(map);
          }
          map.panTo(pos); map.setZoom(15);
        }
        if (markerRef.current) {
          markerRef.current.setPosition(pos);
          if (map) map.setCenter(pos);
          const sp2 = parseFloat(latest.speed) || 0;
          const mc2 = sp2 === 0 ? '#6b7280' : sp2 < 30 ? '#16a34a' : sp2 < 60 ? '#f59e0b' : '#dc2626';
          markerRef.current.setIcon({ path: window.google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: mc2, fillOpacity: 1, strokeWeight: 2, strokeColor: TEAL, anchor: new window.google.maps.Point(0, 0) });
        }
        if (overlayRef.current) overlayRef.current.updatePosition(new window.google.maps.LatLng(pos.lat, pos.lng));
        idx++;
      };
      moveMarker();
      animationIntervalRef.current = setInterval(moveMarker, 1000);
    } catch (err) {
      console.error('[VehicleTracking] fetch error:', err);
      lastEndTimeRef.current = endTime;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleNumber, createPulseOverlay]);

  useEffect(() => {
    if (window.google?.maps) { setMapReady(true); return; }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry`;
    script.async = true;
    script.onload = () => setMapReady(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!mapReady || !mapRef.current || mapInstanceRef.current) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: alert.coordinates
        ? { lat: Number(alert.coordinates.lat), lng: Number(alert.coordinates.lng) }
        : { lat: 17.3850, lng: 78.4867 },
      zoom: alert.coordinates ? 15 : 12,
      mapTypeId: activeLayer,
      mapTypeControl: false, streetViewControl: false,
      fullscreenControl: true, zoomControl: true,
      minZoom: 5, gestureHandling: 'greedy',
    });
    mapInstanceRef.current = map;

    // ── Draw geofence boundary ──
    drawGeofenceBoundary(map);

    // ── Alert event pin ──
    if (alert.coordinates) {
      const { lat, lng } = alert.coordinates;
      new window.google.maps.Marker({
        position: { lat: Number(lat), lng: Number(lng) },
        map,
        icon: {
          url: alert.event === 'ENTERED'
            ? 'https://maps.google.com/mapfiles/ms/icons/green-dot.png'
            : 'https://maps.google.com/mapfiles/ms/icons/red-dot.png'
        },
        title: `${alert.event} at this location`,
        zIndex: 100,
      });
    }

    setIsTracking(true);
    const now = formatDateTime(new Date());
    lastEndTimeRef.current = now;
    fetchVehicleData(now, formatDateTime(new Date()), map);

    trackingIntervalRef.current = setInterval(() => {
      const newEnd = formatDateTime(new Date());
      fetchVehicleData(lastEndTimeRef.current, newEnd, map);
    }, 5000);

    return () => {
      if (trackingIntervalRef.current)  clearInterval(trackingIntervalRef.current);
      if (animationIntervalRef.current) clearInterval(animationIntervalRef.current);
      if (polylineRef.current)          polylineRef.current.setMap(null);
      if (markerRef.current)            markerRef.current.setMap(null);
      if (overlayRef.current)           overlayRef.current.setMap(null);
      if (geofenceOverlayRef.current)   geofenceOverlayRef.current.setMap(null); // ← NEW
      mapInstanceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady]);

  const switchLayer = (type) => {
    if (mapInstanceRef.current) { mapInstanceRef.current.setMapTypeId(type); setActiveLayer(type); setShowLayerPanel(false); }
  };

  const sp         = parseFloat(vehiclePos?.speed) || 0;
  const speedColor = sp === 0 ? '#6b7280' : sp < 30 ? '#16a34a' : sp < 60 ? '#f59e0b' : '#dc2626';
  const speedLabel = sp === 0 ? 'Stopped' : sp < 30 ? 'Slow' : sp < 60 ? 'Moderate' : 'Fast';

  // Geofence info from rawData
  const geoInfo = alert.rawData?.geofence;

  return (
    <>
      <style>{`
        @keyframes vt-modal-in    { from{opacity:0;transform:scale(0.96) translateY(16px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes vt-pulse-ring  { 0%{transform:scale(1);opacity:0.8} 70%{transform:scale(2.4);opacity:0} 100%{transform:scale(2.4);opacity:0} }
        @keyframes vt-spin        { to{transform:rotate(360deg)} }
        @keyframes vt-layer-in    { from{opacity:0;transform:translateY(8px) scale(.97)} to{opacity:1;transform:translateY(0) scale(1)} }
      `}</style>

      <div
        onClick={e => e.target === e.currentTarget && onClose()}
        style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: 'rgba(15,23,42,0.7)', backdropFilter: 'blur(5px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}
      >
        <div style={{
          background: '#fff', borderRadius: 16,
          width: '100%', maxWidth: 1050, height: '88vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 30px 80px rgba(0,0,0,0.4)', animation: 'vt-modal-in 0.22s ease',
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg,#1e293b 0%,#1e6b75 100%)',
            padding: '14px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 38, height: 38, borderRadius: '50%',
                background: 'rgba(42,140,153,0.3)', border: '2px solid rgba(42,140,153,0.7)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
              }}>🚛</div>
              <div>
                <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 16 }}>
                  Live Tracking — {vehicleNumber}
                </div>
                <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>
                  Geofence: <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{alert.geofence}</span>
                  &nbsp;|&nbsp;Event:&nbsp;
                  <span style={{ color: alert.event === 'ENTERED' ? '#4ade80' : '#f87171', fontWeight: 600 }}>
                    {alert.event}
                  </span>
                  &nbsp;|&nbsp;{alert.time}
                  {/* ── NEW: show alertType in header ── */}
                  {geoInfo?.alertType && (
                    <>
                      &nbsp;|&nbsp;Alert Type:&nbsp;
                      <span style={{
                        fontWeight: 700,
                        color: geoInfo.alertType === 'IN' ? '#4ade80' : '#f87171',
                      }}>
                        {geoInfo.alertType === 'IN' ? '↓ IN' : '↑ OUT'}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {vehiclePos && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: `${speedColor}22`, border: `1px solid ${speedColor}55`,
                  borderRadius: 20, padding: '5px 14px',
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: speedColor, display: 'inline-block', animation: 'vt-spin 2s linear infinite' }} />
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>{vehiclePos.speed} km/h</span>
                  <span style={{ color: '#94a3b8', fontSize: 12 }}>{speedLabel}</span>
                </div>
              )}
              <span style={{
                fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 20,
                background: noGpsData ? 'rgba(245,158,11,0.2)' : 'rgba(22,163,74,0.2)',
                color: noGpsData ? '#fbbf24' : '#4ade80',
                border: `1px solid ${noGpsData ? '#f59e0b44' : '#16a34a44'}`,
                display: 'flex', alignItems: 'center', gap: 6,
              }}>
                {noGpsData
                  ? <><span style={{ width: 10, height: 10, border: '2px solid #f59e0b55', borderTop: '2px solid #fbbf24', borderRadius: '50%', display: 'inline-block', animation: 'vt-spin 0.8s linear infinite' }} /> Waiting for GPS…</>
                  : <>● Live Tracking</>
                }
              </span>
              <button onClick={onClose} style={{
                width: 30, height: 30, borderRadius: '50%', border: 'none',
                background: '#475569', color: '#e2e8f0', cursor: 'pointer',
                fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>✕</button>
            </div>
          </div>

          {/* Body: map + sidebar */}
          <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
            {/* Map */}
            <div style={{ flex: 1, position: 'relative' }}>
              {!mapReady && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', flexDirection: 'column', gap: 12 }}>
                  <div style={{ width: 32, height: 32, border: `3px solid #e2e8f0`, borderTop: `3px solid ${TEAL}`, borderRadius: '50%', animation: 'vt-spin 0.8s linear infinite' }} />
                  <span style={{ color: '#6b7280', fontSize: 14 }}>Loading Map…</span>
                </div>
              )}
              <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

              {/* Layer switcher */}
              <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 1100 }}>
                <button
                  onClick={() => setShowLayerPanel(p => !p)}
                  style={{ width: 40, height: 40, border: 'none', background: 'white', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                    <polyline points="2 17 12 22 22 17"/>
                    <polyline points="2 12 12 17 22 12"/>
                  </svg>
                </button>
                {showLayerPanel && (
                  <div style={{ position: 'absolute', top: 45, right: 0, background: 'white', borderRadius: 10, padding: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.14)', minWidth: 160, border: '1px solid #e2e8f0' }}>
                    {GEO_LAYER_OPTIONS.map((opt) => (
                      <div key={opt.id} onClick={() => switchLayer(opt.id)} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', cursor: 'pointer', borderRadius: 7, background: activeLayer === opt.id ? '#f0fdfb' : 'transparent', transition: 'background 0.15s' }}>
                        <span style={{ color: activeLayer === opt.id ? '#1a7a73' : '#94a3b8' }}>{opt.icon}</span>
                        <span style={{ fontSize: 13, fontWeight: activeLayer === opt.id ? 600 : 400, color: activeLayer === opt.id ? '#1a7a73' : '#334155' }}>{opt.label}</span>
                        {activeLayer === opt.id && <span style={{ marginLeft: 'auto', width: 7, height: 7, borderRadius: '50%', background: '#1a7a73' }} />}
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>

            {/* Sidebar */}
            <div style={{
              width: 280, borderLeft: '1px solid #e2e8f0',
              background: '#fff', display: 'flex', flexDirection: 'column',
              overflow: 'hidden', flexShrink: 0,
            }}>
              <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 12 }}>📋 Alert Record</div>
                {[
                  { label: 'Vehicle',  value: alert.vehicle,  icon: '🚛' },
                  { label: 'Geofence', value: alert.geofence, icon: '📍' },
                  { label: 'Event',    value: alert.event,    icon: alert.event === 'ENTERED' ? '🟢' : '🔴' },
                  { label: 'Alert Date and Time', value: alert.time, icon: '🕐' },
                  { label: 'Status',   value: alert.acked ? 'Acknowledged' : 'Pending', icon: alert.acked ? '✅' : '⚠️' },
                ].map(({ label, value, icon }) => (
                  <div key={label} style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 14, flexShrink: 0 }}>{icon}</span>
                    <div>
                      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                      <div style={{ fontSize: 13, color: '#1e293b', fontWeight: 500, marginTop: 1 }}>{value}</div>
                    </div>
                  </div>
                ))}

                {/* Alert Location */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-start' }}>
                  <img src="https://maps.google.com/mapfiles/ms/icons/green-dot.png" alt="pin" style={{ width: 18, height: 18, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Alert Location</div>
                    <div style={{ fontSize: 12, color: '#475569', fontWeight: 500, marginTop: 1, wordBreak: 'break-word' }}>{resolvedAddress}</div>
                  </div>
                </div>

                {/* ── NEW: Geofence Boundary Info ── */}
                {geoInfo && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 14, flexShrink: 0 }}>🗺️</span>
                    <div>
                      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Geofence Boundary</div>
                      <div style={{ fontSize: 12, color: '#475569', fontWeight: 500, marginTop: 1 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ width: 10, height: 10, borderRadius: '50%', background: geoInfo.color || '#1a7a73', display: 'inline-block', flexShrink: 0 }} />
                          {geoInfo.name} ({geoInfo.geometry?.type})
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── NEW: Alert Type ── */}
                {geoInfo?.alertType && (
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 14, flexShrink: 0 }}>🚦</span>
                    <div>
                      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Alert Type</div>
                      <div style={{ marginTop: 3 }}>
                        <AlertTypeBadge type={geoInfo.alertType} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Live vehicle position */}
              <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                  Live Position
                </div>
                {vehiclePos ? (
                  <>
                    {[
                      { label: 'Latitude',  value: vehiclePos.latitude?.toFixed(6)  },
                      { label: 'Longitude', value: vehiclePos.longitude?.toFixed(6) },
                      { label: 'Speed',     value: `${vehiclePos.speed} km/h`       },
                    ].map(({ label, value }) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7, fontSize: 13 }}>
                        <span style={{ color: '#94a3b8' }}>{label}</span>
                        <span style={{ color: '#1e293b', fontWeight: 600 }}>{value}</span>
                      </div>
                    ))}
                  </>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '12px 0' }}>
                    <div style={{ width: 28, height: 28, border: `3px solid #e2e8f0`, borderTop: `3px solid ${TEAL}`, borderRadius: '50%', animation: 'vt-spin 0.8s linear infinite' }} />
                    <span style={{ color: '#94a3b8', fontSize: 12 }}>Fetching GPS data…</span>
                  </div>
                )}
              </div>

              {/* Speed indicator */}
              {vehiclePos && (
                <div style={{ padding: '14px 16px' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Speed Status</div>
                  <div style={{
                    background: `${speedColor}15`, border: `1px solid ${speedColor}40`,
                    borderRadius: 10, padding: '12px 14px',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <div style={{ width: 14, height: 14, borderRadius: '50%', background: speedColor, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 20, fontWeight: 700, color: speedColor }}>{vehiclePos.speed} km/h</div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>{speedLabel}</div>
                    </div>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <div style={{ height: 6, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: 99,
                        background: `linear-gradient(90deg, #16a34a, ${speedColor})`,
                        width: `${Math.min((sp / 120) * 100, 100)}%`,
                        transition: 'width 0.5s ease',
                      }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                      <span>0</span><span>60</span><span>120 km/h</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ── Geofence Map Modal ─────────────────────────────────────────────────────────
const GeofenceMapModal = ({ geofence, onClose }) => {
  const mapRef               = useRef(null);
  const mapInstanceRef       = useRef(null);
  const vehicleMarkersRef    = useRef({});
  const vehiclePolylineRef   = useRef({});
  const trackingIntervalRef  = useRef({});
  const animationRef         = useRef({});
  const lastEndTimeRef       = useRef({});
  const wsRef                = useRef(null);
  const lastGeofenceStateRef = useRef({});
  const geofenceOverlaysRef  = useRef([]);
  const transitLayerRef      = useRef(null);
  const mapContainerRef      = useRef(null);
  const vehicleOverlaysRef   = useRef({});
  const geocoderRef          = useRef(null);
  const autocompleteRef      = useRef(null);
  const searchTimerRef       = useRef(null);

  const [alerts,            setAlerts]            = useState([]);
  console.log("alll",alerts)
  const [vehiclePositions,  setVehiclePositions]  = useState({});
  const [mapReady,          setMapReady]          = useState(false);
  const [isTracking,        setIsTracking]        = useState(false);
  const [connectionStatus,  setConnectionStatus]  = useState('polling');
  const [noGpsData,         setNoGpsData]         = useState({});
  const [activeLayer,       setActiveLayer]       = useState('roadmap');
  const [showLayerPanel,    setShowLayerPanel]    = useState(false);
  const [showTransit,       setShowTransit]       = useState(false);
  const [isFullscreen,      setIsFullscreen]      = useState(false);
  const [selectedVehicle,   setSelectedVehicle]   = useState(null);
  const [dataFetchAttempts, setDataFetchAttempts] = useState({});
  const [searchQuery,       setSearchQuery]       = useState('');
  const [searchResults,     setSearchResults]     = useState([]);
  const [isSearching,       setIsSearching]       = useState(false);

  console.log("data",dataFetchAttempts)
  console.log("isTracking1",isTracking)

  const geofences      = geofence.geofences || [geofence];
  const vehicles       = geofence.vehicles || geofence.rawVehicles || [];
  const vehicleNumbers = vehicles.map(v => v.vehicleNumber).filter(Boolean);

  useEffect(() => {
    if (vehicleNumbers.length > 0) {
      const initialNoGps    = {};
      const initialAttempts = {};
      vehicleNumbers.forEach(vn => { initialNoGps[vn] = true; initialAttempts[vn] = 0; });
      setNoGpsData(initialNoGps);
      setDataFetchAttempts(initialAttempts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // const shouldShowGpsLoader = (() => {
  //   if (vehicleNumbers.length === 0) return false;
  //   if (selectedVehicle) return noGpsData[selectedVehicle] !== false;
  //   return vehicleNumbers.every(vn => noGpsData[vn] !== false);
  // })();

  const setHyderabadBounds = (map) => {
    if (!window.google || !map) return;
    const bounds = new window.google.maps.LatLngBounds(
      { lat: 17.2, lng: 78.3 }, { lat: 17.5, lng: 78.6 }
    );
    map.fitBounds(bounds);
  };

  const formatDateTime = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
           `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R    = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a    = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const validateGPSPoints = (points) => {
    if (!points || points.length === 0) return [];
    if (points.length <= 2) return points;
    const valid = [points[0]];
    for (let i = 1; i < points.length - 1; i++) {
      const cur  = points[i], prev = valid[valid.length - 1];
      const curLat  = parseFloat(cur.latitude),  curLng  = parseFloat(cur.longitude);
      const prevLat = parseFloat(prev.latitude), prevLng = parseFloat(prev.longitude);
      if (isNaN(curLat) || isNaN(curLng) || isNaN(prevLat) || isNaN(prevLng)) continue;
      const dist     = calculateDistance(prevLat, prevLng, curLat, curLng);
      const timeDiff = Math.abs(new Date(cur.vehicletrackingtime) - new Date(prev.vehicletrackingtime)) / 3600000;
      if (timeDiff < 0.001) continue;
      if (dist <= 5 && dist / timeDiff <= 200) valid.push(cur);
    }
    if (points.length > 1) valid.push(points[points.length - 1]);
    return valid;
  };

  const userInteractedRef = useRef(false);

  const searchLocation = (query) => {
    if (!query.trim() || !autocompleteRef.current) { setSearchResults([]); return; }
    setIsSearching(true);
    autocompleteRef.current.getPlacePredictions(
      { input: query, componentRestrictions: { country: 'in' } },
      (predictions, status) => {
        setIsSearching(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSearchResults(predictions);
        } else {
          setSearchResults([]);
        }
      }
    );
  };

  const selectSearchResult = (place) => {
    setSearchQuery(place.description);
    setSearchResults([]);
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ placeId: place.place_id }, (results, status) => {
      if (status === 'OK' && results[0] && mapInstanceRef.current) {
        const viewport = results[0].geometry.viewport;
        if (viewport) {
          mapInstanceRef.current.fitBounds(viewport);
        } else {
          const loc = results[0].geometry.location;
          mapInstanceRef.current.setCenter({ lat: loc.lat(), lng: loc.lng() });
          mapInstanceRef.current.setZoom(15);
        }
      }
    });
  };

  const handleSearchInputChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => searchLocation(val), 500);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  };

  const createVehicleOverlay = (position, speed, vehicleNumber, vehicleColor) => {
    if (!window.google) return null;
    const sp = parseFloat(speed) || 0;
    let speedColor = sp === 0 ? '#6b7280' : sp < 30 ? '#16a34a' : sp < 60 ? '#f59e0b' : '#dc2626';
    const speedLabel = sp === 0 ? 'STOP' : `${Math.round(sp)} km/h`;

    class PulseOverlay extends window.google.maps.OverlayView {
      constructor(pos) { super(); this.pos = pos; this.div = null; }
      onAdd() {
        const div = document.createElement('div');
        div.className = 'vehicle-pulse-wrapper';
        div.innerHTML = `
          <div class="vehicle-pulse-dot">
            <div class="vehicle-pulse-ring"  style="width:20px;height:20px;background:${vehicleColor};opacity:0.3;"></div>
            <div class="vehicle-pulse-ring2" style="width:20px;height:20px;background:${vehicleColor};opacity:0.15;"></div>
            <div style="width:20px;height:20px;border-radius:50%;background:${speedColor};border:3px solid ${vehicleColor};position:relative;z-index:2;box-shadow:0 2px 8px rgba(0,0,0,0.3);"></div>
            <div class="vehicle-speed-badge" style="background:${speedColor};">${speedLabel}</div>
          </div>`;
        this.div = div;
        this.getPanes().overlayMouseTarget.appendChild(div);
      }
      draw() {
        if (!this.div) return;
        const point = this.getProjection().fromLatLngToDivPixel(this.pos);
        if (point) { this.div.style.left = `${point.x}px`; this.div.style.top = `${point.y}px`; }
      }
      updatePosition(newPos) { this.pos = newPos; this.draw(); }
      updateSpeed(newSpeed, vColor) {
        const s  = parseFloat(newSpeed) || 0;
        let sc   = s === 0 ? '#6b7280' : s < 30 ? '#16a34a' : s < 60 ? '#f59e0b' : '#dc2626';
        const lbl = s === 0 ? 'STOP' : `${Math.round(s)} km/h`;
        if (this.div) {
          const dot   = this.div.querySelector('.vehicle-pulse-dot > div:nth-child(3)');
          const ring1 = this.div.querySelector('.vehicle-pulse-ring');
          const ring2 = this.div.querySelector('.vehicle-pulse-ring2');
          const badge = this.div.querySelector('.vehicle-speed-badge');
          if (dot)   dot.style.background   = sc;
          if (ring1) ring1.style.background = vColor;
          if (ring2) ring2.style.background = vColor;
          if (badge) { badge.style.background = sc; badge.textContent = lbl; }
        }
      }
      onRemove() { if (this.div?.parentNode) { this.div.parentNode.removeChild(this.div); this.div = null; } }
    }
    return new PulseOverlay(new window.google.maps.LatLng(position[0], position[1]));
  };

  const createVehicleMarker = (position, speed, map, vehicleNumber, color = null) => {
    if (!window.google) return null;
    const sp     = parseFloat(speed) || 0;
    const colors = ['#16a34a', '#1a7a73', '#dc2626', '#0f4a45', '#ea580c', '#0891b2'];
    const hash   = vehicleNumber.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const vehicleColor = color || colors[hash % colors.length];
    let markerColor    = sp === 0 ? '#6b7280' : sp < 30 ? '#16a34a' : sp < 60 ? '#f59e0b' : '#dc2626';
    const circleIcon   = { path: window.google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: markerColor, fillOpacity: 1, strokeWeight: 1, strokeColor: vehicleColor, anchor: new window.google.maps.Point(0, 0) };
    const marker       = new window.google.maps.Marker({ position: { lat: position[0], lng: position[1] }, map, icon: circleIcon, title: vehicleNumber, animation: window.google.maps.Animation.DROP, zIndex: 99999 });
    const infoWindow   = new window.google.maps.InfoWindow({
      content: `<div style="font-weight:bold;padding:8px;"><div style="font-size:14px;margin-bottom:4px;">🚛 ${vehicleNumber}</div><div style="font-size:12px;">Speed: ${speed || '--'} km/h</div></div>`
    });
    marker.addListener('click', () => { infoWindow.open(map, marker); setSelectedVehicle(vehicleNumber); });
    const overlay = createVehicleOverlay([position[0], position[1]], speed, vehicleNumber, vehicleColor);
    if (overlay) { overlay.setMap(map); vehicleOverlaysRef.current[vehicleNumber] = overlay; }
    return marker;
  };

  const startMarkerAnimation = (coords, speed, vehicleNumber) => {
    if (!coords || coords.length === 0 || !mapInstanceRef.current || !window.google) return;
    if (animationRef.current[vehicleNumber]) { clearInterval(animationRef.current[vehicleNumber]); delete animationRef.current[vehicleNumber]; }
    let idx = 0;
    const map = mapInstanceRef.current;
    if (!vehicleMarkersRef.current[vehicleNumber]) {
      vehicleMarkersRef.current[vehicleNumber] = createVehicleMarker(coords[0], speed, map, vehicleNumber);
      if (!userInteractedRef.current && vehicleNumber === vehicleNumbers[0]) { map.panTo({ lat: coords[0][0], lng: coords[0][1] }); map.setZoom(15); }
    }
    const moveMarker = () => {
      if (idx >= coords.length) { clearInterval(animationRef.current[vehicleNumber]); delete animationRef.current[vehicleNumber]; return; }
      const currentPos = { lat: coords[idx][0], lng: coords[idx][1] };
      if (vehicleMarkersRef.current[vehicleNumber]) {
        vehicleMarkersRef.current[vehicleNumber].setPosition(currentPos);
        if (!userInteractedRef.current || selectedVehicle === vehicleNumber) mapInstanceRef.current?.setCenter(currentPos);
        const sp   = parseFloat(speed) || 0;
        let mc     = sp === 0 ? '#6b7280' : sp < 30 ? '#16a34a' : sp < 60 ? '#f59e0b' : '#dc2626';
        const cols = ['#16a34a', '#1a7a73', '#dc2626', '#0f4a45', '#ea580c', '#0891b2'];
        const hash = vehicleNumber.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
        const vc   = cols[hash % cols.length];
        vehicleMarkersRef.current[vehicleNumber].setIcon({ path: window.google.maps.SymbolPath.CIRCLE, scale: 10, fillColor: mc, fillOpacity: 1, strokeWeight: 1, strokeColor: vc, anchor: new window.google.maps.Point(0, 0) });
        vehicleMarkersRef.current[vehicleNumber].setZIndex(99999);
        if (vehicleOverlaysRef.current[vehicleNumber]) {
          vehicleOverlaysRef.current[vehicleNumber].updatePosition(new window.google.maps.LatLng(currentPos.lat, currentPos.lng));
          vehicleOverlaysRef.current[vehicleNumber].updateSpeed(speed, vc);
        }
      }
      idx++;
    };
    moveMarker();
    animationRef.current[vehicleNumber] = setInterval(moveMarker, 1000);
  };

  const pointInPolygon = (lat, lng, polygonCoords) => {
    let inside = false;
    const x = lng, y = lat;
    for (let i = 0, j = polygonCoords.length - 1; i < polygonCoords.length; j = i++) {
      const xi = typeof polygonCoords[i].lng === 'function' ? polygonCoords[i].lng() : polygonCoords[i].lng;
      const yi = typeof polygonCoords[i].lat === 'function' ? polygonCoords[i].lat() : polygonCoords[i].lat;
      const xj = typeof polygonCoords[j].lng === 'function' ? polygonCoords[j].lng() : polygonCoords[j].lng;
      const yj = typeof polygonCoords[j].lat === 'function' ? polygonCoords[j].lat() : polygonCoords[j].lat;
      const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  };

  const distToSegment = (lat, lng, aLat, aLng, bLat, bLng) => {
    const dx = bLng - aLng, dy = bLat - aLat;
    const lenSq = dx * dx + dy * dy;
    let t = lenSq === 0 ? 0 : ((lng - aLng) * dx + (lat - aLat) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    return calculateDistance(lat, lng, aLat + t * dy, aLng + t * dx);
  };

  const pointNearLine = (lat, lng, latlngs, thresholdKm = 0.05) => {
    for (let i = 0; i < latlngs.length - 1; i++) {
      if (distToSegment(lat, lng, latlngs[i][0], latlngs[i][1], latlngs[i + 1][0], latlngs[i + 1][1]) <= thresholdKm) return true;
    }
    return false;
  };

  const checkGeofences = (validatedPoints, vehicleNumber) => {
    if (!geofences || geofences.length === 0 || !window.google) return;
    validatedPoints.forEach((p) => {
      const pLat = parseFloat(p.latitude), pLng = parseFloat(p.longitude);
      geofences.forEach((geo) => {
        const geomType = geo.geometry?.type;
        let inside = false;
        if (geomType === 'Circle') {
          inside = calculateDistance(pLat, pLng, geo.geometry.center.lat, geo.geometry.center.lng) <= (geo.geometry.radius || 500) / 1000;
        } else if (geomType === 'Polygon' || geomType === 'polygon') {
          let path = [];
          if (geo.geometry.latlngs) path = geo.geometry.latlngs.map(([lat, lng]) => ({ lat, lng }));
          else if (geo.geometry.coordinates) {
            const c = geo.geometry.coordinates;
            path = (Array.isArray(c[0][0]) ? c[0] : c).map(([lng, lat]) => ({ lat, lng }));
          }
          if (path.length >= 3) inside = pointInPolygon(pLat, pLng, path);
        } else if (['LineString', 'linestring', 'Polyline', 'polyline'].includes(geomType)) {
          const latlngs = geo.geometry.latlngs || (geo.geometry.coordinates || []).map(([lng, lat]) => [lat, lng]);
          if (latlngs.length >= 2) inside = pointNearLine(pLat, pLng, latlngs, 0.05);
        } else return;
        const stateKey = `${vehicleNumber}_${geo.name || 'Zone'}`;
        const newType  = inside ? 'entry' : 'exit';
        if (lastGeofenceStateRef.current[stateKey] === newType) return;
        lastGeofenceStateRef.current[stateKey] = newType;
        setAlerts(prev => [{
          id: `${Date.now()}-${Math.random()}-${vehicleNumber}`,
          type: newType,
          message: inside
            ? `🟢 Vehicle ${vehicleNumber} entered geofence "${geo.name || 'Zone'}"`
            : `🔴 Vehicle ${vehicleNumber} exited geofence "${geo.name || 'Zone'}"`,
          timestamp: new Date(p.vehicletrackingtime || Date.now()).toLocaleTimeString(),
          vehicleNumber,
        }, ...prev].slice(0, 50));
      });
    });
  };

  const fetchVehicleData = async (vehicleNumber, startTime, endTime, map) => {
    try {
      setDataFetchAttempts(prev => ({ ...prev, [vehicleNumber]: (prev[vehicleNumber] || 0) + 1 }));
      const newData = await postDataWithBody(fetchVehicleTracking, { vehicleNumber, startTime, endTime }, {});
      lastEndTimeRef.current[vehicleNumber] = endTime;
      if (!newData || newData.length === 0) return;
      const validated = validateGPSPoints(newData);
      if (validated.length === 0) return;

      setNoGpsData(prev => {
        if (prev[vehicleNumber] === false) return prev;
        return { ...prev, [vehicleNumber]: false };
      });

      const latest = validated[validated.length - 1];
      setVehiclePositions(prev => ({ ...prev, [vehicleNumber]: { latitude: parseFloat(latest.latitude), longitude: parseFloat(latest.longitude), speed: latest.speed ?? '--', timestamp: latest.vehicletrackingtime } }));
      const coords = validated.map(p => [parseFloat(p.latitude), parseFloat(p.longitude)]);
      if (map && coords.length > 1 && window.google) {
        if (vehiclePolylineRef.current[vehicleNumber]) vehiclePolylineRef.current[vehicleNumber].setMap(null);
        const colors = ['#0bedf5', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899'];
        vehiclePolylineRef.current[vehicleNumber] = new window.google.maps.Polyline({ path: coords.map(([lat, lng]) => ({ lat, lng })), geodesic: false, strokeColor: colors[vehicleNumbers.indexOf(vehicleNumber) % colors.length], strokeOpacity: 0.9, strokeWeight: 3, map, zIndex: 500 });
      }
      startMarkerAnimation(coords, latest.speed, vehicleNumber);
      checkGeofences(validated, vehicleNumber);
    } catch (err) {
      console.error(`[GeofenceModal] Polling error for ${vehicleNumber}:`, err);
      lastEndTimeRef.current[vehicleNumber] = endTime;
    }
  };

  const startVehicleTracking = (map) => {
    if (!map || vehicleNumbers.length === 0) return;
    setIsTracking(true);
    const now = formatDateTime(new Date());
    vehicleNumbers.forEach((vehicleNumber, i) => {
      lastEndTimeRef.current[vehicleNumber] = now;
      setTimeout(() => fetchVehicleData(vehicleNumber, now, formatDateTime(new Date()), map), i * 1000);
      if (trackingIntervalRef.current[vehicleNumber]) clearInterval(trackingIntervalRef.current[vehicleNumber]);
      trackingIntervalRef.current[vehicleNumber] = setInterval(() => {
        fetchVehicleData(vehicleNumber, lastEndTimeRef.current[vehicleNumber], formatDateTime(new Date()), map);
      }, 5000);
    });
  };

  useEffect(() => {
    if (window.google?.maps) { setMapReady(true); return; }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry,drawing`;
    script.async = true; script.defer = true;
    script.onload = () => setMapReady(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!mapReady || !mapRef.current || mapInstanceRef.current) return;
    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: 17.3850, lng: 78.4867 }, zoom: 12, mapTypeId: activeLayer,
      disableDefaultUI: true, streetViewControl: true, minZoom: 5, gestureHandling: 'greedy',
    });
    mapInstanceRef.current = map;
    transitLayerRef.current = new window.google.maps.TransitLayer();
    if (window.google?.maps?.places) {
      geocoderRef.current     = new window.google.maps.Geocoder();
      autocompleteRef.current = new window.google.maps.places.AutocompleteService();
    }
    setHyderabadBounds(map);
    let mapInitialized = false;
    setTimeout(() => { mapInitialized = true; }, 3000);
    let dragResetTimer = null;
    map.addListener('dragstart', () => { if (mapInitialized) { userInteractedRef.current = true; if (dragResetTimer) clearTimeout(dragResetTimer); } });
    map.addListener('dragend', () => {
      if (!mapInitialized) return;
      dragResetTimer = setTimeout(() => {
        userInteractedRef.current = false;
        const t = selectedVehicle || vehicleNumbers[0];
        if (t && vehicleMarkersRef.current[t]) { const p = vehicleMarkersRef.current[t].getPosition(); if (p) mapInstanceRef.current?.setCenter(p); }
      }, 5000);
    });
    drawAllGeofences(map);
    if (vehicleNumbers.length > 0) startVehicleTracking(map);
    initWebSocket();
    return () => { cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady]);

  const drawAllGeofences = (map) => {
    if (!window.google || !map) return;
    geofenceOverlaysRef.current.forEach(o => o.setMap(null));
    geofenceOverlaysRef.current = [];
    if (!geofences || geofences.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    geofences.forEach((geo) => {
      const geomType = geo.geometry?.type;
      const color    = geo.color || '#1a7a73';
      if (geomType === 'Circle') {
        const { lat, lng } = geo.geometry.center;
        const circle = new window.google.maps.Circle({ strokeColor: color, strokeOpacity: 0.8, strokeWeight: 2, fillColor: color, fillOpacity: 0.15, map, center: { lat, lng }, radius: geo.geometry.radius || 500, clickable: true });
        geofenceOverlaysRef.current.push(circle);
        bounds.union(circle.getBounds());
      } else if (geomType === 'Polygon' || geomType === 'polygon' || geomType === 'Region') {
        let path = [];
        if (geo.geometry.latlngs) path = geo.geometry.latlngs.map(([lat, lng]) => ({ lat, lng }));
        else if (geo.geometry.coordinates) { const c = geo.geometry.coordinates; path = (Array.isArray(c[0][0]) ? c[0] : c).map(([lng, lat]) => ({ lat, lng })); }
        if (path.length < 3) return;
        const polygon = new window.google.maps.Polygon({ paths: path, strokeColor: color, strokeOpacity: 0.8, strokeWeight: 2, fillColor: color, fillOpacity: 0.15, map, clickable: true });
        geofenceOverlaysRef.current.push(polygon);
        path.forEach(p => bounds.extend(p));
      } else if (['LineString', 'linestring', 'Polyline', 'polyline'].includes(geomType)) {
        let path = [];
        if (geo.geometry.latlngs) path = geo.geometry.latlngs.map(([lat, lng]) => ({ lat, lng }));
        else if (geo.geometry.coordinates) path = geo.geometry.coordinates.map(([lng, lat]) => ({ lat, lng }));
        if (path.length < 2) return;
        const polyline = new window.google.maps.Polyline({ path, geodesic: true, strokeColor: color, strokeOpacity: 0.85, strokeWeight: 3, map, clickable: true });
        geofenceOverlaysRef.current.push(polyline);
        path.forEach(p => bounds.extend(p));
      }
    });
    if (!bounds.isEmpty()) map.fitBounds(bounds);
  };

  const initWebSocket = () => {
    try {
      const vehicleNumber = vehicleNumbers[0] || 'TEST001';
      const WS_URL = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws/geofence?vehicleNumber=${encodeURIComponent(vehicleNumber)}&geofenceId=${geofence.id}`;
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;
      ws.onopen    = () => setConnectionStatus('connected');
      ws.onmessage = (event) => { try { handleGeofenceEvent(JSON.parse(event.data)); } catch (e) { console.error('WS parse error:', e); } };
      ws.onerror   = () => setConnectionStatus('polling');
      ws.onclose   = () => setConnectionStatus('polling');
    } catch {
      setConnectionStatus('polling');
    }
  };

  const handleGeofenceEvent = (data) => {
    const { eventType, vehicleNumber, geofenceName, timestamp } = data;
    const isEntry = eventType === 'ENTER' || eventType === 'GEOFENCE_ENTER';
    setAlerts(prev => [{
      id: Date.now() + vehicleNumber,
      type: isEntry ? 'entry' : 'exit',
      message: isEntry
        ? `🟢 Vehicle ${vehicleNumber} entered geofence "${geofenceName}"`
        : `🔴 Vehicle ${vehicleNumber} exited geofence "${geofenceName}"`,
      timestamp: timestamp ? new Date(timestamp).toLocaleTimeString() : new Date().toLocaleTimeString(),
      vehicleNumber,
    }, ...prev].slice(0, 50));
  };

  const cleanup = () => {
    Object.values(trackingIntervalRef.current).forEach(i => i && clearInterval(i));
    trackingIntervalRef.current = {};
    Object.values(animationRef.current).forEach(a => a && clearInterval(a));
    animationRef.current = {};
    if (wsRef.current) wsRef.current.close();
    lastEndTimeRef.current = {};
    geofenceOverlaysRef.current.forEach(o => o.setMap(null));
    geofenceOverlaysRef.current = [];
    Object.values(vehiclePolylineRef.current).forEach(p => p && p.setMap(null));
    vehiclePolylineRef.current = {};
    Object.values(vehicleMarkersRef.current).forEach(m => m && m.setMap(null));
    Object.values(vehicleOverlaysRef.current).forEach(o => o && o.setMap(null));
    vehicleOverlaysRef.current  = {};
    vehicleMarkersRef.current   = {};
    mapInstanceRef.current      = null;
    lastGeofenceStateRef.current = {};
    if (transitLayerRef.current) { transitLayerRef.current.setMap(null); transitLayerRef.current = null; }
  };

  const switchLayer = (layerType) => {
    if (mapInstanceRef.current) { mapInstanceRef.current.setMapTypeId(layerType); setActiveLayer(layerType); setShowLayerPanel(false); }
  };

  useEffect(() => {
    if (!transitLayerRef.current || !mapInstanceRef.current) return;
    transitLayerRef.current.setMap(showTransit ? mapInstanceRef.current : null);
  }, [showTransit]);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  useEffect(() => {
    if (mapInstanceRef.current && window.google?.maps) {
      window.google.maps.event.trigger(mapInstanceRef.current, 'resize');
    }
  }, [isFullscreen]);

  const toggleFullscreen = useCallback(() => {
    const el = mapContainerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen?.();
    else document.exitFullscreen?.();
  }, []);

  const statusLabel = {
    connected:    '● Live',
    connecting:   '◌ Connecting...',
    polling:      '⟳ Polling',
    disconnected: '○ Disconnected',
  };

  const statusBadgeStyle = {
    connected:    { bg: 'rgba(22,163,74,0.25)',    color: '#fff',    border: '#16a34a' },
    connecting:   { bg: 'rgba(245,158,11,0.2)',    color: '#fbbf24', border: '#f59e0b' },
    polling:      { bg: 'rgba(42,140,153,0.2)',    color: '#fff',    border: TEAL      },
    disconnected: { bg: 'rgba(220,38,38,0.2)',     color: '#fff',    border: 'white'   },
  };
  const badgeStyle = statusBadgeStyle[connectionStatus] || statusBadgeStyle.polling;

  const typeIcon = { Circle: '◉', circle: '◉', Polygon: '⬠', polygon: '⬠', Region: '⬠', region: '⬠', LineString: '╌', linestring: '╌', Polyline: '╌', polyline: '╌' }[geofences[0]?.geometry?.type] || '';

  return (
    <>
      <style>{`
        @keyframes gf-modal-in   { from{opacity:0;transform:scale(0.96) translateY(16px)} to{opacity:1;transform:scale(1) translateY(0)} }
        @keyframes gf-pulse-dot  { 0%,100%{box-shadow:0 0 0 2px rgba(30,64,175,.3)} 50%{box-shadow:0 0 0 6px rgba(30,64,175,.1)} }
        @keyframes layer-in      { from{opacity:0;transform:translateY(8px) scale(.97)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes gf-spin       { to{transform:rotate(360deg)} }
        .gf-entry{border-left:4px solid #16a34a;background:#f0fdf4}
        .gf-exit {border-left:4px solid #dc2626;background:#fef2f2}
        .gf-item {padding:8px 10px;border-radius:6px;margin-bottom:6px;font-size:12px}
        .gf-ts   {font-size:10px;color:#9ca3af;margin-top:2px}
        .vehicle-pulse-wrapper{position:absolute;pointer-events:none;transform:translate(-50%,-50%);z-index:99998;}
        .vehicle-pulse-dot{position:relative;display:flex;align-items:center;justify-content:center;}
        .vehicle-pulse-ring{position:absolute;border-radius:50%;animation:pulse-ring 1.8s ease-out infinite;}
        .vehicle-pulse-ring2{position:absolute;border-radius:50%;animation:pulse-ring2 1.8s ease-out 0.4s infinite;}
        .vehicle-speed-badge{position:absolute;top:-28px;left:50%;transform:translateX(-50%);white-space:nowrap;font-size:10px;font-weight:700;padding:2px 7px;border-radius:99px;color:#fff;pointer-events:none;box-shadow:0 2px 6px rgba(0,0,0,0.25);}
        @keyframes pulse-ring{0%{transform:scale(1);opacity:0.8}70%{transform:scale(2.4);opacity:0}100%{transform:scale(2.4);opacity:0}}
        @keyframes pulse-ring2{0%{transform:scale(1);opacity:0.5}70%{transform:scale(1.8);opacity:0}100%{transform:scale(1.8);opacity:0}}
        .vehicle-selector{position:absolute;bottom:16px;right:12px;left:auto;transform:none;z-index:1000;background:rgba(255,255,255,0.96);backdrop-filter:blur(6px);border-radius:12px;padding:8px 10px;box-shadow:0 4px 20px rgba(0,0,0,0.18);display:flex;flex-direction:column;gap:6px;max-width:200px;border:1px solid #e0e7ff;}
        .vehicle-btn{padding:6px 14px;border-radius:20px;border:1px solid #e2e8f0;background:white;cursor:pointer;font-size:12px;font-weight:600;transition:all 0.2s;display:flex;align-items:center;gap:5px;white-space:nowrap;}
        .vehicle-btn:hover:not(.active){background:#ccfbf1;border-color:#0f4a45;}
        .vehicle-btn.active{background:linear-gradient(135deg,#0f4a45,#1a7a73);color:white;border-color:#0f4a45;box-shadow:0 2px 8px rgba(26,122,115,0.35);}
        .vehicle-btn.has-data{border-color:#16a34a;}
      `}</style>

      <div className="row" style={{ margin: 0, alignItems: 'stretch', fontFamily: "'DM Sans','Segoe UI',system-ui,sans-serif" }}>
        {/* Left panel: Geofence Details */}
        <div className="col-lg-4" style={{ display: 'flex', flexDirection: 'column', padding: '14px 8px 14px 0' }}>
          <div style={{ flex: 1, background: '#fff', borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.08)', border: '1px solid #dee2e6', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Gradient header */}
            <div style={{ background: 'linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)', padding: '9px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 26, height: 26, borderRadius: 6, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: 14 }}>📍</span>
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#fff' }}>{geofences[0]?.name || 'Geofence'}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
                    <span style={{ color: geofences[0]?.color || '#c7d2fe', fontWeight: 600 }}>{typeIcon} {geofences[0]?.geometry?.type}</span>
                    {' | '}{geofences.length} geofence{geofences.length !== 1 ? 's' : ''}
                    {' | '}{vehicleNumbers.length} vehicle{vehicleNumbers.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, background: geofences[0]?.status === 'active' ? 'rgba(22,163,74,.2)' : 'rgba(107,114,128,.2)', color: geofences[0]?.status === 'active' ? '#4ade80' : '#9ca3af', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 99, border: `1px solid ${geofences[0]?.status === 'active' ? '#16a34a' : '#6b7280'}` }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: geofences[0]?.status === 'active' ? '#22c55e' : '#6b7280', display: 'inline-block' }} />
                  {geofences[0]?.status === 'active' ? 'Active' : 'Inactive'}
                </span>
                <button onClick={onClose} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>← Back</button>
              </div>
            </div>

            {/* Live status */}
            <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>Live status &amp; alerts</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: badgeStyle.bg, color: badgeStyle.color, border: `1px solid ${badgeStyle.border}`, display: 'flex', alignItems: 'center', gap: 5 }}>
                  {connectionStatus === 'polling' && (
                    <span style={{ width: 8, height: 8, border: `2px solid rgba(42,140,153,0.3)`, borderTop: `2px solid ${TEAL}`, borderRadius: '50%', display: 'inline-block', animation: 'gf-spin 0.8s linear infinite' }} />
                  )}
                  {statusLabel[connectionStatus]}
                </span>
                {geofences[0]?.alertType && <AlertTypeBadge type={geofences[0].alertType} />}
              </div>
            </div>

            {/* Alert Type row */}
            {geofences[0]?.alertType && (
              <div style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>Alert Type</span>
                <AlertTypeBadge type={geofences[0].alertType} />
              </div>
            )}

            {/* Assigned vehicles */}
            {vehicleNumbers.length > 0 && (
              <div style={{ padding: '10px 16px', flex: 1, overflowY: 'auto' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>ASSIGNED VEHICLES ({vehicleNumbers.length})</div>
                {vehicles.map((vehicle) => {
                  const hasGPS   = noGpsData[vehicle.vehicleNumber] === false;
                  const isWaiting = noGpsData[vehicle.vehicleNumber] !== false;
                  const colors   = ['#16a34a', '#1a7a73', '#dc2626', '#0f4a45', '#ea580c'];
                  const vc       = colors[vehicleNumbers.indexOf(vehicle.vehicleNumber) % colors.length];
                  return (
                    <div key={vehicle.id} onClick={() => setSelectedVehicle(vehicle.vehicleNumber)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', background: selectedVehicle === vehicle.vehicleNumber ? '#f0fdf4' : 'transparent', borderRadius: '4px' }}>
                      {isWaiting
                        ? <span style={{ width: 10, height: 10, border: `2px solid #e2e8f0`, borderTop: `2px solid ${TEAL}`, borderRadius: '50%', display: 'inline-block', flexShrink: 0, animation: 'gf-spin 0.8s linear infinite' }} />
                        : <span style={{ width: 10, height: 10, borderRadius: '50%', background: vc, display: 'inline-block', flexShrink: 0 }} />
                      }
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 12, color: '#1e293b' }}>{vehicle.vehicleNumber}</div>
                        <div style={{ fontSize: 10, color: '#64748b' }}>{vehicle.routeName || 'No route'}</div>
                      </div>
                      {hasGPS
                        ? <span style={{ fontSize: 10, color: '#16a34a', fontWeight: 600 }}>● Live</span>
                        : <span style={{ fontSize: 10, color: '#f59e0b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                            <span style={{ width: 8, height: 8, border: `1.5px solid #f59e0b55`, borderTop: `1.5px solid #f59e0b`, borderRadius: '50%', display: 'inline-block', animation: 'gf-spin 0.8s linear infinite' }} />
                            Fetching…
                          </span>
                      }
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right panel: Map */}
        <div className="col-lg-8" style={{ display: 'flex', flexDirection: 'column', padding: '14px 0 14px 8px' }}>
          <div style={{ flex: 1, borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 16px rgba(0,0,0,0.09)', display: 'flex', flexDirection: 'column', minHeight: 520 }}>
            {/* Map header */}
            <div style={{ background: 'linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)', padding: '5px 14px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <div style={{ width: 26, height: 26, borderRadius: 6, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/>
                  <line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/>
                </svg>
              </div>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 13, flex: 1 }}>Geofence Map</span>
              <span style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 20, padding: '2px 9px', fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
                {(GEO_LAYER_OPTIONS.find(o => o.id === activeLayer)?.label ?? activeLayer)} layer
              </span>
            </div>
            {/* Search bar */}
            <div style={{ padding: '8px 12px', background: '#fff', borderBottom: '1px solid #e2e8f0', position: 'relative', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 9, padding: '6px 10px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  placeholder="Search city, area, landmark or coordinates…"
                  style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, color: '#0f172a', background: 'transparent' }}
                />
                {isSearching && (
                  <div style={{ width: 14, height: 14, border: '2px solid #e2e8f0', borderTop: `2px solid ${TEAL}`, borderRadius: '50%', animation: 'gf-spin 0.7s linear infinite', flexShrink: 0 }} />
                )}
                {searchQuery && !isSearching && (
                  <button onClick={clearSearch} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#94a3b8', display: 'flex', alignItems: 'center' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                )}
              </div>
              {searchResults.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 12, right: 12, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '0 0 10px 10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 1050, maxHeight: 260, overflowY: 'auto' }}>
                  {searchResults.map((result) => (
                    <div
                      key={result.place_id}
                      onClick={() => selectSearchResult(result)}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#ef4444" stroke="none" style={{ flexShrink: 0, marginTop: 2 }}>
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                      </svg>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{result.structured_formatting?.main_text || result.description}</div>
                        {result.structured_formatting?.secondary_text && (
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{result.structured_formatting.secondary_text}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Map */}
            <div ref={mapContainerRef} style={{ flex: 1, position: 'relative' }}>
              {!mapReady && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', flexDirection: 'column', gap: 12 }}>
                  <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTop: `3px solid ${TEAL}`, borderRadius: '50%', animation: 'gf-spin 0.8s linear infinite' }} />
                  <span style={{ color: '#6b7280', fontSize: 14 }}>Loading Google Maps…</span>
                </div>
              )}
              <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

              {mapReady && vehicleNumbers.length > 1 && (
                <div className="vehicle-selector">
                  <button className={`vehicle-btn ${!selectedVehicle ? 'active' : ''}`} onClick={() => setSelectedVehicle(null)}>
                    All Vehicles
                    {vehicleNumbers.every(vn => noGpsData[vn] === false) ? <span style={{ color: '#16a34a' }}>●</span> : <span style={{ color: '#f59e0b' }}>⏳</span>}
                  </button>
                  {vehicleNumbers.map(vn => (
                    <button key={vn} className={`vehicle-btn ${selectedVehicle === vn ? 'active' : ''} ${noGpsData[vn] === false ? 'has-data' : ''}`} onClick={() => setSelectedVehicle(vn)}>
                      {vn.substring(0, 8)}...
                      {noGpsData[vn] === false ? <span style={{ color: '#16a34a' }}>●</span> : <span style={{ color: '#f59e0b' }}>⏳</span>}
                    </button>
                  ))}
                </div>
              )}

              {/* Left-side vertical controls: Zoom only */}
              <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 1200, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <button onClick={() => mapInstanceRef.current?.setZoom((mapInstanceRef.current?.getZoom() || 12) + 1)} title="Zoom in" style={{ width: 36, height: 36, borderRadius: 9, background: '#fff', border: '1.5px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,.12)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, color: '#374151', fontWeight: 400, lineHeight: 1 }}>+</button>
                <button onClick={() => mapInstanceRef.current?.setZoom((mapInstanceRef.current?.getZoom() || 12) - 1)} title="Zoom out" style={{ width: 36, height: 36, borderRadius: 9, background: '#fff', border: '1.5px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,.12)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, color: '#374151', fontWeight: 400, lineHeight: 1 }}>−</button>
              </div>

              {/* Fullscreen button */}
              <button
                onClick={toggleFullscreen}
                title={isFullscreen ? "Exit fullscreen" : "Full screen"}
                style={{
                  position: 'absolute', top: 10, right: 10, zIndex: 1200,
                  width: 40, height: 40, borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: isFullscreen ? '#1a7a73' : 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                {isFullscreen ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 3v3a2 2 0 0 1-2 2H3"/><path d="M21 8h-3a2 2 0 0 1-2-2V3"/>
                    <path d="M3 16h3a2 2 0 0 1 2 2v3"/><path d="M16 21v-3a2 2 0 0 1 2-2h3"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/>
                    <path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/>
                  </svg>
                )}
              </button>

              {/* Layer switcher */}
              <div style={{ position: 'absolute', top: 60, right: 10, zIndex: 1200 }}>
                <button onClick={() => setShowLayerPanel(p => !p)} style={{ width: 40, height: 40, border: 'none', background: 'white', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                    <polyline points="2 17 12 22 22 17"/>
                    <polyline points="2 12 12 17 22 12"/>
                  </svg>
                </button>
                {showLayerPanel && (
                  <div style={{ position: 'absolute', top: 45, right: 0, background: 'white', borderRadius: 10, padding: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.14)', minWidth: 160, border: '1px solid #e2e8f0' }}>
                    {GEO_LAYER_OPTIONS.map((opt) => (
                      <div key={opt.id} onClick={() => switchLayer(opt.id)} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 10px', cursor: 'pointer', borderRadius: 7, background: activeLayer === opt.id ? '#f0fdfb' : 'transparent', transition: 'background 0.15s' }}>
                        <span style={{ color: activeLayer === opt.id ? '#1a7a73' : '#94a3b8' }}>{opt.icon}</span>
                        <span style={{ fontSize: 13, fontWeight: activeLayer === opt.id ? 600 : 400, color: activeLayer === opt.id ? '#1a7a73' : '#334155' }}>{opt.label}</span>
                        {activeLayer === opt.id && <span style={{ marginLeft: 'auto', width: 7, height: 7, borderRadius: '50%', background: '#1a7a73' }} />}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Transit toggle */}
              <button
                onClick={() => setShowTransit(p => !p)}
                title={showTransit ? "Hide transit" : "Show transit"}
                style={{
                  position: 'absolute', top: 110, right: 10, zIndex: 1200,
                  width: 40, height: 40, borderRadius: 8, border: 'none', cursor: 'pointer',
                  background: showTransit ? '#1a7a73' : 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                  stroke={showTransit ? '#fff' : '#555'} strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="2" width="16" height="16" rx="2"/>
                  <path d="M4 10h16"/><path d="M8 2v8"/><path d="M16 2v8"/>
                  <path d="M8 18l-2 4"/><path d="M16 18l2 4"/>
                </svg>
              </button>

              {/* Vehicle speed chips — stacked bottom-left, growing upward */}
              <div style={{ position: 'absolute', bottom: 16, left: 12, zIndex: 1000, display: 'flex', flexDirection: 'column-reverse', gap: 6, maxHeight: '60%', overflowY: 'auto', pointerEvents: 'none' }}>
                {Object.entries(vehiclePositions).map(([vn, pos]) => {
                  if (selectedVehicle && selectedVehicle !== vn) return null;
                  const sp2      = parseFloat(pos.speed) || 0;
                  const chipBg   = sp2 === 0 ? '#6b7280' : sp2 < 30 ? '#16a34a' : sp2 < 60 ? '#f59e0b' : '#dc2626';
                  const spLabel2 = sp2 === 0 ? 'STOPPED' : sp2 < 30 ? 'SLOW' : sp2 < 60 ? 'MODERATE' : 'FAST';
                  const colors   = ['#16a34a', '#1a7a73', '#dc2626', '#0f4a45', '#ea580c'];
                  const vc       = colors[vehicleNumbers.indexOf(vn) % colors.length];
                  return (
                    <div key={vn} style={{ background: chipBg, color: '#fff', borderRadius: 8, padding: '7px 12px', boxShadow: `0 2px 12px ${chipBg}66`, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, borderLeft: `4px solid ${vc}`, whiteSpace: 'nowrap' }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: vc, display: 'inline-block', flexShrink: 0 }} />
                      🚛 {vn}
                      <span style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 6, padding: '2px 8px', fontSize: 11 }}>{pos.speed ?? '--'} km/h</span>
                      <span style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 6, padding: '2px 8px', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{spLabel2}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ── Geofences Table ────────────────────────────────────────────────────────────
const GeofencesTable = ({ data, onView, loading }) => {
  const [sortCol, setSortCol] = useState('id');
const [sortDir, setSortDir] = useState('desc');
  const [page,    setPage]    = useState(1);
  const [pageSize,setPageSize]= useState(10);

  const handleSort = (key) => {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(key); setSortDir('asc'); }
    setPage(1);
  };

  const sorted = [...data].sort((a, b) => {
    const av = a[sortCol], bv = b[sortCol];
    const cmp = typeof av === 'number' ? av - bv : String(av ?? '').localeCompare(String(bv ?? ''));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const safePage  = Math.min(page, Math.max(1, Math.ceil(sorted.length / pageSize)));
  const paginated = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div style={styles.card}>
      <div style={{ overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead>
            <tr>
              {GEOFENCE_COLS.map(col => (
                <th key={col.key} style={{ ...styles.th, cursor: col.sortable ? 'pointer' : 'default' }} onClick={() => col.sortable && handleSort(col.key)}>
                  {col.label}
                  {col.sortable && <SortIcon column={col.key} sortCol={sortCol} sortDir={sortDir} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? <TableLoader cols={GEOFENCE_COLS.length} /> :
              paginated.length === 0 ? (
                <tr><td colSpan={GEOFENCE_COLS.length} style={{ ...styles.td, textAlign: 'center', color: '#94a3b8', padding: '32px 16px' }}>No geofence data found</td></tr>
              ) : paginated.map((row, i) => (
                <tr key={row.id} className="gf-table-row" style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: row.color, display: 'inline-block', flexShrink: 0 }} />
                      <span style={{ fontWeight: 500, color: '#1e293b' }}>{row.name}</span>
                    </div>
                  </td>
                  <td style={{ ...styles.td, color: '#475569' }}>{row.geometry?.type}</td>
                  <td style={styles.td}><div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#475569' }}>🚗 {row.vehicleCount || 0}</div></td>
                  <td style={styles.td}><AlertTypeBadge type={row.alertType} /></td>
                  <td style={styles.td}><StatusBadge status={row.status} /></td>
                  <td style={styles.td}>
                    <button onClick={() => onView(row)} style={{ ...styles.actionBtn, ...styles.actionView }} title="View Geofence">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <Pagination total={sorted.length} page={safePage} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
    </div>
  );
};

// ── Alerts Table ───────────────────────────────────────────────────────────────
const AlertsList = ({ data: rawData, onAck, loading, error, onRefresh }) => {
  const [sortCol, setSortCol] = useState('time');
  const [sortDir, setSortDir] = useState('desc');
  const [page,    setPage]    = useState(1);
  const [pageSize,setPageSize]= useState(10);
  const [alertsWithAddress, setAlertsWithAddress] = useState([]);
  const [resolvingAddress,  setResolvingAddress]  = useState(false);
  const [ackTarget,         setAckTarget]         = useState(null);
  const [trackingAlert,     setTrackingAlert]     = useState(null);

  useEffect(() => {
    const resolveAddresses = async () => {
      if (!rawData.length) { setAlertsWithAddress([]); return; }
      setResolvingAddress(true);
      const updated = [...rawData];
      for (let i = 0; i < updated.length; i++) {
        if (updated[i].coordinates) {
          try {
            const address = await reverseGeocode(updated[i].coordinates.lat, updated[i].coordinates.lng);
            updated[i] = { ...updated[i], address };
          } catch { }
        }
      }
      setAlertsWithAddress(updated);
      setResolvingAddress(false);
    };
    resolveAddresses();
  }, [rawData]);

  const handleSort = (key) => {
    if (sortCol === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(key); setSortDir('asc'); }
    setPage(1);
  };

  const displayData = alertsWithAddress.length ? alertsWithAddress : rawData;

  const sorted = [...displayData].sort((a, b) => {
    const av = sortCol === 'time' ? a.rawTime : a[sortCol];
    const bv = sortCol === 'time' ? b.rawTime : b[sortCol];
    const cmp = typeof av === 'boolean'
      ? (av === bv ? 0 : av ? 1 : -1)
      : typeof av === 'number'
        ? av - bv
        : String(av ?? '').localeCompare(String(bv ?? ''));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const safePage  = Math.min(page, Math.max(1, Math.ceil(sorted.length / pageSize)));
  const paginated = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  const ackAlertData = ackTarget ? (displayData.find(a => a.id === ackTarget) || null) : null;

  return (
    <>
      <div style={styles.card}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '11px 18px', borderBottom: '1px solid #f1f5f9',
          background: '#fafbfd',
        }}>
          <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              background: '#f1f5f9', borderRadius: 20, padding: '2px 10px',
              fontWeight: 700, color: '#475569', fontSize: 12,
            }}>
              {rawData.length} alert{rawData.length !== 1 ? 's' : ''}
            </span>
            loaded
            {resolvingAddress && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 11, height: 11, border: '2px solid #e2e8f0', borderTop: `2px solid ${TEAL}`, borderRadius: '50%', animation: 'spin 0.6s linear infinite', display: 'inline-block' }} />
                <span style={{ fontSize: 11, color: '#94a3b8' }}>Resolving addresses…</span>
              </span>
            )}
          </span>
          <button
            onClick={onRefresh}
            disabled={loading}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '6px 16px', fontSize: 12, fontWeight: 700,
              border: `1.5px solid ${TEAL}`, borderRadius: 8,
              background: loading ? '#f1f5f9' : '#fff',
              color: TEAL, cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#eef6fc'; }}
            onMouseLeave={e => { if (!loading) e.currentTarget.style.background = '#fff'; }}
          >
            {loading ? '⟳ Refreshing…' : '⟳ Refresh'}
          </button>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', borderBottom: '1px solid #fecaca', padding: '8px 16px', color: '#dc2626', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                {ALERT_COLS.map(col => (
                  <th key={col.key} style={{ ...styles.th, cursor: col.sortable ? 'pointer' : 'default' }} onClick={() => col.sortable && handleSort(col.key)}>
                    {col.label}
                    {col.sortable && <SortIcon column={col.key} sortCol={sortCol} sortDir={sortDir} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <TableLoader cols={ALERT_COLS.length} /> :
                paginated.length === 0 ? (
                  <tr><td colSpan={ALERT_COLS.length} style={{ ...styles.td, textAlign: 'center', color: '#94a3b8', padding: '32px 16px' }}>No alert records found</td></tr>
                ) : paginated.map((alert, i) => (
                  <tr key={alert.id} className="gf-table-row" style={{ background: alert.acked ? (i % 2 === 0 ? '#fff' : '#f8fafc') : '#fffbeb' }}>
                    <td style={{ ...styles.td, color: '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>{alert.alertRefId}</td>
                    <td style={styles.td}>
                      <button
                        onClick={() => setTrackingAlert(alert)}
                        title="Click to view live tracking"
                        style={{ background: 'none', border: 'none', padding: 0, fontWeight: 700, color: TEAL, fontSize: 13, cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3, display: 'flex', alignItems: 'center', gap: 5 }}
                        onMouseEnter={e => e.currentTarget.style.color = '#1e6b75'}
                        onMouseLeave={e => e.currentTarget.style.color = TEAL}
                      >
                        {alert.vehicle}
                      </button>
                    </td>
                    <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                      <button
                        onClick={() => setTrackingAlert(alert)}
                        title="Click to view live tracking"
                        style={{ background: 'none', border: 'none', padding: 0, fontWeight: 500, color: '#1e293b', fontSize: 13, cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3, display: 'inline-block', verticalAlign: 'middle', wordBreak: 'keep-all' }}
                        onMouseEnter={e => e.currentTarget.style.color = '#0f172a'}
                        onMouseLeave={e => e.currentTarget.style.color = '#1e293b'}
                      >
                        {alert.geofence}
                      </button>
                    </td>
                    <td style={styles.td}><EventBadge event={alert.event} /></td>
                    <td style={{ ...styles.td, color: '#64748b', fontSize: 13, maxWidth: '450px', minWidth: '280px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                        <span style={{ fontSize: 12, opacity: 0.7, flexShrink: 0 }}>📍</span>
                        <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>{alert.address}</span>
                      </div>
                    </td>
                    <td style={{ ...styles.td, color: '#94a3b8', fontSize: 13 }}>{alert.time}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: alert.acked ? '#dcfce7' : '#fef9c3', color: alert.acked ? '#16a34a' : '#ca8a04' }}>
                        {alert.acked ? 'Acknowledged' : 'Pending'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {!alert.acked
                        ? <button onClick={() => setAckTarget(alert.id)} style={styles.ackBtn}>✓ Ack</button>
                        : <span style={{ color: '#16a34a', fontSize: 13 }}>✓ Done</span>
                      }
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <Pagination total={sorted.length} page={safePage} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
      </div>

      {ackTarget && (
        <AckConfirmModal
          alert={ackAlertData}
          onConfirm={() => { onAck(ackTarget); setAckTarget(null); }}
          onCancel={() => setAckTarget(null)}
        />
      )}

      {trackingAlert && (
        <VehicleTrackingModal
          alert={trackingAlert}
          onClose={() => setTrackingAlert(null)}
        />
      )}
    </>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────────
const GeofenceManagement = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(
    ['alerts', 'deviations'].includes(location.state?.activeTab) ? location.state.activeTab : 'geofences'
  );
  const [geofences,    setGeofences]    = useState([]);
  const [geoLoading,   setGeoLoading]   = useState(false);
  const [geoError,     setGeoError]     = useState(null);
  const [alerts,       setAlerts]       = useState([]);
  const [alertLoading, setAlertLoading] = useState(false);
  const [alertError,   setAlertError]   = useState(null);
  const [search,       setSearch]       = useState('');
  const [viewGeofence, setViewGeofence] = useState(null);

  useEffect(() => { fetchGeofences(); }, []);

  const fetchGeofences = async () => {
    setGeoLoading(true); setGeoError(null);
    try {
      const res  = await getData(getgeofenceall);
      const json = (res && typeof res === 'object' && 'data' in res) ? res
        : (res && typeof res.json === 'function') ? await res.json() : res;
      if (json?.success && Array.isArray(json.data)) {
        setGeofences(json.data.map(g => ({
          ...g,
          status:       g.status ? g.status.toLowerCase() : getStatusFromActive(g.active),
          vehicleCount: (g.vehicles || []).length,
          rawVehicles:  g.vehicles || [],
          alertType:    g.alertType || null,   // ← preserve alertType from API
        })));
      } else throw new Error(json?.message || 'Unexpected geofence response');
    } catch (err) {
      setGeoError(err.message);
    } finally {
      setGeoLoading(false);
    }
  };

  useEffect(() => {
    if ((activeTab === 'alerts' || activeTab === 'deviations') && alerts.length === 0 && !alertLoading) {
      fetchAlerts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);
 

  const fetchAlerts = async () => {
    setAlertLoading(true); setAlertError(null);
    try {
      const res = await getData(getalertsall);
      if (Array.isArray(res))                        setAlerts(res.map(normaliseAlert));
      else if (res?.success && Array.isArray(res.data)) setAlerts(res.data.map(normaliseAlert));
      else throw new Error(res?.message || 'Unexpected alert response');
    } catch (err) {
      setAlertError(err.message);
    } finally {
      setAlertLoading(false);
    }
  };

  const handleAck = async (id) => {
    try {
      const res = await putDataWithBody(`${ackupdate}/${id}`, '', {});
      if (res.status === 'success') {
        toast.success(res.msg, { position: toast.POSITION.TOP_CENTER, autoClose: 2000 });
        setAlerts(prev => prev.map(a => a.id === id ? { ...a, acked: true } : a));
      }
    } catch (err) {
      toast.error(`Failed to acknowledge alert: ${err.msg}`);
    }
  };

  const filteredGeofences = geofences.filter(g => {
    if (!search) return true;
    const q = search.toLowerCase();
    return g.name?.toLowerCase().includes(q) || g.zone?.toLowerCase().includes(q) ||
           g.geometry?.type?.toLowerCase().includes(q) || g.status?.toLowerCase().includes(q);
  });

  // Regular alerts (entry/exit + anything else) vs. deviation alerts — split by alertType
  const regularAlerts   = alerts.filter(a => !isDeviationAlert(a));
  const deviationAlerts = alerts.filter(isDeviationAlert);
  const alertsForTab     = activeTab === 'deviations' ? deviationAlerts : regularAlerts;

  const filteredAlerts = alertsForTab.filter(a => {
    if (!search) return true;
    const q = search.toLowerCase();
    return a.vehicle?.toLowerCase().includes(q) || a.geofence?.toLowerCase().includes(q) ||
           a.event?.toLowerCase().includes(q)   || a.address?.toLowerCase().includes(q);
  });

  const stats = activeTab === 'geofences'
    ? getGeofenceStats(geofences)
    : getAlertStats(alertsForTab);

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={styles.page} className="gf-page">
        <div style={styles.statsRow}>
          {stats.map((s, i) => <StatCard key={`${activeTab}-${i}`} {...s} />)}
        </div>

        {activeTab === 'geofences' && geoError && (
          <div style={styles.errorBanner}>
            ⚠️ Failed to load geofences: {geoError}
            <button onClick={fetchGeofences} style={styles.retryBtn}>Retry</button>
          </div>
        )}

        <div style={styles.tabBar}>
          {/* Tab Pills */}
          <div style={styles.tabs}>
            {[
              { id: 'geofences',  label: 'Geofences',       icon: '🗺️' },
              { id: 'alerts',     label: 'Alerts',           icon: '🔔' },
              { id: 'deviations', label: 'Deviation Alerts', icon: '🚨' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setSearch(''); setViewGeofence(null); }}
                style={{ ...styles.tab, ...(activeTab === tab.id ? styles.tabActive : {}) }}
              >
                <span style={{ fontSize: 13 }}>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search + Result count */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              display: 'flex', alignItems: 'center',
              background: '#fff', border: '1.5px solid #e2e8f0',
              borderRadius: 10, padding: '7px 14px', gap: 8,
              boxShadow: '0 2px 10px rgba(26,122,115,.07)', minWidth: 280,
              transition: 'border-color 0.2s',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={activeTab === 'geofences' ? 'Search by name, type…' : 'Search by vehicle, geofence…'}
                style={{
                  border: 'none', outline: 'none', fontSize: 13,
                  color: '#1e293b', background: 'transparent', width: '100%',
                  fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                }}
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 15, lineHeight: 1, padding: 0, display: 'flex', alignItems: 'center' }}
                >×</button>
              )}
            </div>
            {search && (
              <span style={{
                fontSize: 12, color: '#64748b', whiteSpace: 'nowrap',
                background: '#f1f5f9', borderRadius: 20, padding: '4px 10px',
                fontWeight: 600,
              }}>
                {activeTab === 'geofences'
                  ? `${filteredGeofences.length} result${filteredGeofences.length !== 1 ? 's' : ''}`
                  : `${filteredAlerts.length} result${filteredAlerts.length !== 1 ? 's' : ''}`}
              </span>
            )}
          </div>
        </div>

        {viewGeofence ? (
          <GeofenceMapModal geofence={viewGeofence} onClose={() => setViewGeofence(null)} />
        ) : activeTab === 'geofences' ? (
          <GeofencesTable data={filteredGeofences} onView={setViewGeofence} loading={geoLoading} />
        ) : (
          <AlertsList data={filteredAlerts} onAck={handleAck} loading={alertLoading} error={alertError} onRefresh={fetchAlerts} />
        )}
      </div>
    </>
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────
const styles = {
  page:       { fontFamily: "'DM Sans','Segoe UI',system-ui,sans-serif", padding: '24px 28px', background: 'linear-gradient(145deg, #f0f4ff 0%, #f8faff 40%, #eef2ff 70%, #f5f7ff 100%)', minHeight: '100vh', color: '#1e293b' },
  statsRow:   { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 18, marginBottom: 24 },
  statCard:   { background: '#fff', border: '1.5px solid #e0f4f2', borderRadius: 14, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 4px 20px rgba(26,122,115,.08)' },
  statIcon:   { width: 38, height: 38, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  statValue:  { fontSize: 22, fontWeight: 800, lineHeight: 1, color: '#0f172a', letterSpacing: '-0.02em', flexShrink: 0 },
  statLabel:  { fontSize: 11.5, color: '#94a3b8', fontWeight: 500, lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  tabBar:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  tabs:       { display: 'flex', gap: 3, background: '#f1f5f9', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '4px' },
  tab:        { padding: '7px 20px', border: 'none', borderRadius: 7, background: 'transparent', color: '#64748b', fontWeight: 500, fontSize: 13.5, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, transition: 'background 0.15s', letterSpacing: '0.01em' },
  tabActive:  { background: 'linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%)', color: '#fff', fontWeight: 700, boxShadow: '0 2px 10px rgba(26,122,115,.32)' },
  tabBadge:   { background: '#ef4444', color: '#fff', borderRadius: 99, fontSize: 10.5, fontWeight: 700, padding: '1px 7px', letterSpacing: '0.02em' },
  card:       { background: '#fff', border: '1.5px solid #e0f4f2', borderRadius: 6, boxShadow: '0 4px 20px rgba(26,122,115,.07)' },
  table:      { width: '100%', borderCollapse: 'collapse' },
  th:         { position: 'sticky', top: 0, zIndex: 2, padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%)', whiteSpace: 'nowrap', userSelect: 'none', borderRight: '1px solid rgba(255,255,255,.12)', height: 46, textTransform: 'uppercase', letterSpacing: '0.06em' },
  td:         { padding: '13px 16px', fontSize: 13.5, color: '#475569', borderBottom: '1px solid #f1f5f9' },
  zonePill:   { background: '#f1f5f9', color: '#475569', borderRadius: 6, padding: '3px 10px', fontSize: 13, fontWeight: 500 },
  badge:      { padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600 },
  actionBtn:  { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, border: '1.5px solid', borderRadius: 8, cursor: 'pointer', background: '#fff', transition: 'all .15s' },
  actionView: { borderColor: '#99f6e4', color: '#1a7a73' },
  arrowCircle:{ width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  eventBadge: { padding: '3px 11px', borderRadius: 6, fontSize: 12, fontWeight: 700 },
  ackBtn:     { background: 'linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%)', border: 'none', color: 'white', fontSize: '12px', fontWeight: '700', padding: '6px 16px', borderRadius: '6px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'opacity 0.2s', minWidth: '80px' },
  errorBanner:{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 18px', marginBottom: 16, color: '#dc2626', fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 },
  retryBtn:   { marginLeft: 'auto', padding: '4px 14px', border: '1.5px solid #dc2626', borderRadius: 6, background: 'transparent', color: '#dc2626', cursor: 'pointer', fontSize: 12, fontWeight: 700 },
};


const pg = {
  bar:        { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderTop: '1.5px solid #e0f4f2', background: '#fafbfd', flexWrap: 'wrap', gap: 8 },
  info:       { fontSize: 12.5, color: '#64748b' },
  controls:   { display: 'flex', alignItems: 'center', gap: 6 },
  label:      { fontSize: 12.5, color: '#64748b', whiteSpace: 'nowrap' },
  input:      { width: 44, padding: '5px 6px', fontSize: 13, border: '1.5px solid #e2e8f0', borderRadius: 6, textAlign: 'center', outline: 'none', color: '#1e293b', background: '#fff' },
  select:     { padding: '5px 8px', fontSize: 12.5, border: '1.5px solid #e2e8f0', borderRadius: 6, background: '#fff', color: '#1e293b', cursor: 'pointer', outline: 'none' },
  navBtn:     { width: 30, height: 30, border: '1.5px solid #e2e8f0', borderRadius: 6, background: '#fff', fontSize: 18, color: '#475569', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, lineHeight: 1, padding: 0 },
  activeBtn:  { width: 30, height: 30, border: 'none', borderRadius: 6, background: 'linear-gradient(135deg, #0f4a45 0%, #1a7a73 100%)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'default', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  totalPages: { fontSize: 12.5, color: '#64748b', padding: '4px 11px', border: '1.5px solid #e2e8f0', borderRadius: 6, background: '#f8fafc' },
};

export default GeofenceManagement;